<template>
  <div class="tool-page">
    <section class="tool-header">
      <h1>{{ $t('website.tools.timesheet.title') }}</h1>
      <p>
        {{ $t('website.tools.timesheet.description') }}
      </p>
    </section>

    <section class="tool-cards">
      <div class="tool-card secondary">
        <h3>🚀 {{ $t('website.tools.timesheet.digitalise_time_tracking') }}</h3>
        <p>
          {{ $t('website.tools.timesheet.test') }} <strong>ChronoFox</strong>
          {{ $t('website.tools.timesheet.our_smart_software') }}
        </p>
        <NuxtLink to="/pricing" class="btn-secondary">
          <FontAwesome
            icon="fa-solid fa-chevron-right"
            style="margin-right: 4px"
          />
          {{ $t('website.tools.timesheet.learn_more') }}
        </NuxtLink>
      </div>

      <div class="tool-card">
        <h2>📝 {{ $t('website.tools.timesheet.free_excel_template') }}</h2>
        <p>
          {{ $t('website.tools.timesheet.document_your_times') }}
        </p>
        <button class="btn-primary" @click="downloadFile">
          <FontAwesome icon="fa-solid fa-download" style="margin-right: 4px" />
          {{ $t('website.tools.timesheet.download_for_free') }}
        </button>
      </div>
    </section>

    <section class="tool-text">
      <h4>{{ $t('website.tools.timesheet.why_excel_is_often_the_start') }}</h4>
      <p>
        {{
          $t('website.tools.timesheet.why_excel_is_often_the_start_description')
        }}
        <strong>ChronoFox</strong>
        {{ $t('website.tools.timesheet.is_your_next_logical_step') }}
      </p>
    </section>

    <section class="tool-text">
      <h4>
        {{
          $t('website.tools.timesheet.when_to_choose_chronofox_and_not_excel')
        }}
      </h4>
      <p>
        {{
          $t(
            'website.tools.timesheet.when_to_choose_chronofox_and_not_excel_description'
          )
        }}
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: 'website',
});

import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();

import logo from '@/assets/logo/chronofox_wortbild_right.png';
useSeoMeta({
  // General
  title: t('website.tools.timesheet.page_title') + ' | ' + t('app_name'),
  description: t('website.tools.timesheet.page_description'),
  keywords: t('website.tools.timesheet.page_keywords'),

  // Open Graph
  ogTitle: t('website.tools.timesheet.page_title') + ' | ' + t('app_name'),
  ogDescription: t(' website.tools.timesheet.page_description'),
  ogLocale: locale.value ? locale.value : 'de',
  ogType: 'website',
  ogImage: logo,

  // Twitter (X)
  twitterCard: 'summary_large_image',
  twitterTitle: t('website.tools.timesheet.page_title') + ' | ' + t('app_name'),
  twitterDescription: t('website.tools.timesheet.page_description'),
  twitterImage: logo,
  twitterSite: '@ChronoFoxApp',
});

const downloadFile = () => {
  const link = document.createElement('a');
  link.href = '/tools/ChronoFox_TimeSheet.xlsx';
  link.download = 'ChronoFox_TimeSheet.xlsx';
  link.click();
};
</script>

<style scoped>
.tool-page {
  padding: 4rem 5%;
  max-width: 1400px;
  margin: auto;
  display: flex;
  flex-direction: column;
  gap: 3rem;
  font-family: 'Inter', sans-serif;
}

.tool-header h1 {
  font-size: 2.75rem;
  margin-bottom: 1rem;
  background: linear-gradient(
    to right,
    var(--orange-2),
    var(--yellow-orange-2)
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.tool-header p {
  font-size: 1.125rem;
  color: #555;
  max-width: 700px;
}

.tool-cards {
  display: flex;
  flex-direction: row;
  gap: 2rem;
  justify-content: center;
}

.tool-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 1rem;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 123, 255, 0.1);
}

.tool-card.secondary {
  background: rgba(108, 117, 125, 0.1);
  border: 1px solid #ccc;
}

.tool-card h2,
.tool-card h3 {
  font-size: 1.75rem;
  margin-bottom: 1rem;
}

.tool-card p {
  font-size: 1.1rem;
  color: #333;
}

.tool-text {
  text-align: center;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
}

.tool-text h4 {
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  color: var(--t-accent);
}

.btn-primary,
.btn-secondary {
  margin-top: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border-radius: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
}

.btn-primary {
  background-color: var(--t-accent);
  color: white;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-primary:hover {
  background-color: var(--brown-grey-1);
}

.btn-secondary:hover {
  background-color: var(--t-accent);
}

@media (max-width: 1000px) {
  .tool-cards {
    flex-direction: column;
    gap: 1rem;
    word-wrap: break-word;
  }

  .tool-card {
    width: 100%;
  }
}
</style>
